import { getTimezoneOffset } from 'date-fns-tz';
import * as R from 'ramda';
import { Plover } from '@blisspointmedia/bpm-types/dist/webTrafficOptimization';
import { convert12hrTo24hr, convert24hrTo12hr } from '../../../../utils/time-utils';
import { defaultDowRow, WtoRule } from '../../wto-form-context';
import {
  capitalize,
  formatTimestampOffset,
  parseTimestampDowOperations,
  setFormattedTime,
  setTimestampTime
} from '../../wto-utils';
import { MILLISECONDS_IN_HOUR, TIMEZONE_MAP } from './rules-form-modal-constants';
import { isVariantOption } from './rules-form-modal-utils';

export const createDowHandlers = (
  newRule: WtoRule,
  setNewRule: (rule: WtoRule) => void,
  savedDow: ReturnType<typeof parseTimestampDowOperations>,
  wtoFormObj: Plover,
  readOnly: boolean,
  setValidationErrors: (errors: string[]) => void
) => {
  const handleDowTimezone = (timezone: string | null, index: number) => {
    if (timezone) {
      const newRuleFormObj = R.clone(newRule);

      const newStartValues = { ...savedDow[index].start };
      const newEndValues = { ...savedDow[index].end };

      const startOffset =
        getTimezoneOffset(
          TIMEZONE_MAP[timezone],
          new Date(
            !newRule.dow_operation[index].start_time
              ? setTimestampTime(newStartValues)
              : newRule.dow_operation[index].start_time
          )
        ) / MILLISECONDS_IN_HOUR;
      const endOffset =
        getTimezoneOffset(
          TIMEZONE_MAP[timezone],
          new Date(
            !newRule.dow_operation[index].end_time
              ? setTimestampTime(newEndValues)
              : newRule.dow_operation[index].end_time
          )
        ) / MILLISECONDS_IN_HOUR;

      newStartValues.offset = formatTimestampOffset(startOffset);
      newEndValues.offset = formatTimestampOffset(endOffset);

      newRuleFormObj.dow_operation[index].time_zone = timezone;
      newRuleFormObj.dow_operation[index].start_time = setTimestampTime(newStartValues);
      newRuleFormObj.dow_operation[index].formattedStartTime = setFormattedTime(newStartValues);
      newRuleFormObj.dow_operation[index].end_time = setTimestampTime(newEndValues);
      newRuleFormObj.dow_operation[index].formattedEndTime = setFormattedTime(newEndValues);

      setNewRule(newRuleFormObj);
    }
  };

  const handleDowTime = (
    value: string | null,
    type: 'hour' | 'minute',
    bound: 'start' | 'end',
    index: number
  ) => {
    if (value) {
      const newRuleFormObj = R.clone(newRule);

      const newValues = { ...savedDow[index][bound] };

      newValues.hour12 = type === 'hour' ? Number(value).toString() : savedDow[index][bound].hour12;
      newValues.minute = type === 'minute' ? value.padStart(2, '0') : savedDow[index][bound].minute;

      [newValues.hour24] = convert12hrTo24hr(
        `${newValues.hour12}:${newValues.minute}${savedDow[index][bound].period}`
      ).split(':');

      newRuleFormObj.dow_operation[index][`${bound}_time`] = setTimestampTime(newValues);
      newRuleFormObj.dow_operation[index][`formatted${capitalize(bound)}Time`] =
        setFormattedTime(newValues);

      setNewRule(newRuleFormObj);
    }
  };

  const handleDowPeriod = (period: 'am' | 'pm' | null, bound: 'start' | 'end', index: number) => {
    if (period) {
      const newRuleFormObj = R.clone(newRule);

      const newValues = { ...savedDow[index][bound] };

      newValues.period = period;

      if (period === 'am' && newValues.hour24 >= 12) {
        newValues.hour24 = (Number(newValues.hour24) - 12).toString().padStart(2, '0');
      } else if (period === 'pm' && newValues.hour24 < 12) {
        newValues.hour24 = (Number(newValues.hour24) + 12).toString().padStart(2, '0');
      }

      [newValues.hour12] = convert24hrTo12hr(`${newValues.hour24}:${newValues.minute}`).split(':');

      newRuleFormObj.dow_operation[index][`${bound}_time`] = setTimestampTime(newValues);
      newRuleFormObj.dow_operation[index][`formatted${capitalize(bound)}Time`] =
        setFormattedTime(newValues);

      setNewRule(newRuleFormObj);
    }
  };

  const addDayOfWeekSplitRow = () => {
    const newRuleFormObj = R.clone(newRule);
    const newDowOperation = R.clone(defaultDowRow);
    newRuleFormObj.dow_operation.push(newDowOperation);
    setNewRule(newRuleFormObj);
  };

  const addDowTrafficSplit = (index: number) => {
    const newRuleFormObj = R.clone(newRule);
    const dowVariants = R.clone(newRuleFormObj.dow_operation[index].variants);
    if (dowVariants.length < wtoFormObj.variants.length) {
      dowVariants.push({ name: '', url: '', split: 0 });
    } else {
      setValidationErrors([
        'There are no more variants for this test to split against; to add more, return to the Naming & Variants step'
      ]);
    }
    newRuleFormObj.dow_operation[index].variants = dowVariants;
    setNewRule(newRuleFormObj);
  };

  const handleDowVariant = (
    operation: 'select' | 'allocate' | 'delete',
    index: number,
    rowIndex?: number,
    data?: unknown
  ) => {
    const newRuleFormObj = R.clone(newRule);
    let dowRows = R.clone(newRuleFormObj.dow_operation);
    let dowVariants = R.clone(newRuleFormObj.dow_operation[index].variants);

    switch (operation) {
      case 'select':
        if (rowIndex !== undefined && isVariantOption(data)) {
          dowVariants[rowIndex].name = data.variant_id;
          dowVariants[rowIndex].url = data.variant_url as string;
        }
        newRuleFormObj.cookie_operation_url = [];
        break;
      case 'allocate':
        if (rowIndex !== undefined) {
          dowVariants[rowIndex].split = Number(data);
        }
        break;
      case 'delete':
        if (rowIndex !== undefined) {
          dowVariants = dowVariants.filter((_, variantIndex) => variantIndex !== rowIndex);
        } else {
          dowRows = dowRows.filter((_, rowIdx) => rowIdx !== index);
        }
        newRuleFormObj.cookie_operation_url = [];
        break;
      default:
        break;
    }

    if (rowIndex !== undefined) {
      newRuleFormObj.dow_operation[index].variants = dowVariants;
    } else {
      newRuleFormObj.dow_operation = dowRows;
    }
    setNewRule(newRuleFormObj);
  };

  const selectDay = (day: { number: number; label: string }, index: number) => {
    if (!readOnly) {
      const newRuleFormObj = R.clone(newRule);
      let dowSelections = [...newRuleFormObj.dow_operation[index].day];
      const dayIndex = dowSelections.indexOf(day.number);
      if (dayIndex === -1) {
        dowSelections.push(day.number);
      } else {
        dowSelections = dowSelections.filter((_, selectionIndex) => selectionIndex !== dayIndex);
      }
      newRuleFormObj.dow_operation[index].day = dowSelections;
      setNewRule(newRuleFormObj);
    }
  };

  return {
    handleDowTimezone,
    handleDowTime,
    handleDowPeriod,
    addDayOfWeekSplitRow,
    addDowTrafficSplit,
    handleDowVariant,
    selectDay
  };
};
